import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
import { RecordResponse } from '../models/ServiceTypes';
import { WARootState } from './store';
import { MimeType } from '@/utils/fileValidation';
import { createTypedBlob } from '@/utils/triggerBlobDownload';

export interface ContactBase {
  CompleteAccountVisibility: boolean;
  CompleteDocumentVisibility: boolean;
  ContactId: number;
  AdvisorId: number | null;
  IsCreatedByUser: boolean;
  IsConfirmed: boolean;
  CanImpersonate: boolean;
  IsPrimaryAdvisor: boolean;
  IsAdvisorPrimaryContact: boolean;
  FirstName: string;
  LastName: string;
  FullName: string;
  EmailAddress: string;
  Mobile: string | null;
  Work: string | null;
  Title: string | null;
  Photo: string | null;
  FirmName: string | null;
  FirmAddress1: string | null;
  FirmAddress2: string | null;
  FirmCity: string | null;
  FirmZip: string | null;
  FirmState: string | null;
  FirmWebsite: string | null;
  SendUserInvite?: boolean;
}

/**
 * A contact returned by `getContacts`, carrying a client-side identity.
 *
 * Advisors and contacts are displayed in the same list and neither `AdvisorId`
 * nor `ContactId` is unique across it, so `teamMemberKey` composes both. It is
 * derived from the record's own ids (not its position) so it survives a refetch
 * after a contact is added or deleted.
 */
export interface TeamMemberRecord extends ContactBase {
  teamMemberKey: string;
}

/** Body accepted by `updateContact` — tolerates the client-only identity, which is stripped before the request. */
export type ContactUpdatePayload = ContactBase & { teamMemberKey?: string };

export type ContactsResponse = RecordResponse<ContactBase>;
export type TeamMembersResponse = RecordResponse<TeamMemberRecord>;

/**
 * Assign each contact a stable identity. Records whose id pair collides get a
 * deterministic occurrence suffix so keys stay unique within the response.
 */
export const withTeamMemberKeys = (
  contacts: ContactBase[],
): TeamMemberRecord[] => {
  const occurrences = new Map<string, number>();

  return contacts.map((contact) => {
    const ids = `${String(contact.AdvisorId ?? 'none')}-${String(contact.ContactId)}`;
    const seen = occurrences.get(ids) ?? 0;
    occurrences.set(ids, seen + 1);

    return {
      ...contact,
      teamMemberKey: seen === 0 ? ids : `${ids}-${String(seen)}`,
    };
  });
};

export interface AccountPermission {
  AdvisorId: number;
  WealthType: number;
  WealthId: number;
  WealthAccountId?: number;
  IsAllowedToSee: boolean;
  IsClosed: boolean;
  Name: string;
  Accounts?: AccountPermission[];
  Children?: AccountPermission[];
  IsParent?: boolean;
  Level: number;
  CanEdit?: boolean;
}

export interface DocumentPermission {
  AdvisorId: number;
  VaultFileId: number;
  Name: string;
  Label: string;
  IsFolder: boolean;
  CanEdit: boolean;
  AllowedToSee: boolean;
  Extension: string;
  Children: DocumentPermission[];
}

export type AccountPermissionsResponse = RecordResponse<AccountPermission>;
export type DocumentPermissionsResponse = RecordResponse<DocumentPermission>;

export const contactsApi = createApi({
  reducerPath: 'contactsApi',
  baseQuery: waServicesQuery,
  tagTypes: ['Contacts', 'ContactAccountPermissions'],

  endpoints: (builder) => ({
    getContacts: builder.query<TeamMembersResponse, void>({
      queryFn: async (_arg, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const investorId = state.appView.UserInfo?.UserId;

        const result = await baseQuery({
          url: `/api/${currentRole}/contacts`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        const contactsWithPhotos = await Promise.all(
          (result.data as ContactsResponse).Records.map(async (contact) => {
            if (!contact.Photo) return contact;

            let processedPhoto = contact.Photo.trim();
            if (processedPhoto.includes('/quicklinks/')) {
              processedPhoto = processedPhoto.replace(
                '/quicklinks/',
                '/contacts/',
              );
            }

            try {
              const photoResult = await baseQuery({
                url: processedPhoto,
                params: { investorId },
                method: 'GET',
                responseHandler: async (response) => response.blob(),
              });

              if (photoResult.data) {
                const blob = createTypedBlob(
                  photoResult.data as Blob,
                  null,
                  MimeType.Jpeg,
                );
                return {
                  ...contact,
                  Photo: URL.createObjectURL(blob),
                };
              }
            } catch (error) {
              console.error(
                'Error loading photo for contact:',
                contact.ContactId,
                error,
              );
            }

            return contact;
          }),
        );

        return {
          data: {
            ...(result.data as ContactsResponse),
            Records: withTeamMemberKeys(contactsWithPhotos),
          },
        };
      },
      extraOptions: { maxRetries: 3 },
      providesTags: (result) =>
        result ? [{ type: 'Contacts', id: 'LIST' }] : ['Contacts'],
    }),
    deleteContact: builder.mutation<void, number>({
      queryFn: async (contactId, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const result = await baseQuery({
          url: `/api/${currentRole}/contacts/${contactId}`,
          method: 'DELETE',
        });

        if (result.error) {
          return { error: result.error } as any;
        }

        return { data: undefined } as any;
      },
      invalidatesTags: ['Contacts'],
      extraOptions: { maxRetries: 0 },
    }),
    createContact: builder.mutation<ContactBase, Partial<ContactBase>>({
      queryFn: async (body, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const result = await baseQuery({
          url: `/api/${currentRole}/contacts`,
          method: 'POST',
          body,
        });

        if (result.error) {
          return { error: result.error } as any;
        }

        return { data: result.data as ContactBase };
      },
      invalidatesTags: ['Contacts'],
      extraOptions: { maxRetries: 0 },
    }),
    getContactAccountPermissions: builder.query<
      AccountPermissionsResponse,
      number
    >({
      queryFn: async (advisorId, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const result = await baseQuery({
          url: `/api/${currentRole}/contacts/${advisorId}/permissions/accounts`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error } as any;
        }

        return { data: result.data as AccountPermissionsResponse };
      },
      extraOptions: { maxRetries: 3 },
      providesTags: (_result, _error, advisorId) => [
        { type: 'ContactAccountPermissions', id: advisorId },
      ],
    }),
    getContactDocumentPermissions: builder.query<
      DocumentPermissionsResponse,
      number
    >({
      queryFn: async (advisorId, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const result = await baseQuery({
          url: `/api/${currentRole}/contacts/${advisorId}/permissions/documents`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error } as any;
        }

        return { data: result.data as DocumentPermissionsResponse };
      },
      extraOptions: { maxRetries: 3 },
    }),
    updateContact: builder.mutation<ContactBase, ContactUpdatePayload>({
      queryFn: async (body, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const contactIdentifier = body.AdvisorId ?? body.ContactId;

        // `teamMemberKey` is a client-side identity, not part of the contract.
        const { teamMemberKey: _teamMemberKey, ...contact } = body;

        const result = await baseQuery({
          url: `/api/${currentRole}/contacts/${contactIdentifier}`,
          method: 'PUT',
          body: contact,
        });

        if (result.error) {
          return { error: result.error } as any;
        }

        return { data: result.data as ContactBase };
      },
      invalidatesTags: ['Contacts'],
      extraOptions: { maxRetries: 0 },
    }),
    updateContactAccountPermissions: builder.mutation<
      AccountPermission | { [key: string]: any },
      AccountPermission | { [key: string]: any }
    >({
      queryFn: async (body, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const advisorId = (body as any).AdvisorId ?? 0;

        const result = await baseQuery({
          url: `/api/${currentRole}/contacts/${advisorId}/permissions/accounts/`,
          method: 'PUT',
          body,
        });

        if (result.error) {
          return { error: result.error } as any;
        }

        return { data: result.data as any };
      },
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (_result, _error, body) => {
        const advisorId = (body as AccountPermission).AdvisorId;
        return advisorId
          ? [{ type: 'ContactAccountPermissions', id: advisorId }]
          : ['ContactAccountPermissions'];
      },
    }),
    /*
     * Update a single document or folder permission for a contact (advisor)
     */
    updateContactDocumentPermission: builder.mutation<
      DocumentPermission | { [key: string]: any },
      DocumentPermission | { [key: string]: any }
    >({
      queryFn: async (body, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const advisorId = (body as any).AdvisorId ?? 0;
        const vaultFileId = (body as any).VaultFileId ?? 0;

        const result = await baseQuery({
          url: `/api/${currentRole}/contacts/${advisorId}/permissions/documents/${vaultFileId}`,
          method: 'PUT',
          body,
        });

        if (result.error) {
          return { error: result.error } as any;
        }

        return { data: result.data as any };
      },
      extraOptions: { maxRetries: 0 },
    }),
  }),
});

export const {
  useGetContactsQuery,
  useCreateContactMutation,
  useGetContactAccountPermissionsQuery,
  useGetContactDocumentPermissionsQuery,
  useUpdateContactMutation,
  useUpdateContactAccountPermissionsMutation,
  useUpdateContactDocumentPermissionMutation,
  useDeleteContactMutation,
} = contactsApi;
